const vertexShaderSource = `
attribute vec2 a_position;
attribute vec2 a_texCoord;

varying vec2 v_texCoord;

void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
  v_texCoord = a_texCoord;
}
`;

const fragmentShaderSource = `
precision mediump float;

uniform sampler2D u_source;
uniform float u_minGreen;
uniform float u_greenBias;
uniform float u_softness;
uniform float u_spill;
uniform float u_minSaturation;
uniform float u_spillDominance;
uniform float u_spillMinGreen;

varying vec2 v_texCoord;

void main() {
  vec4 texel = texture2D(u_source, v_texCoord);
  float red = texel.r * 255.0;
  float green = texel.g * 255.0;
  float blue = texel.b * 255.0;
  float minChannel = min(red, min(green, blue));
  float maxRedBlue = max(red, blue);
  float maxChannel = max(red, max(green, blue));
  float saturation = maxChannel == 0.0
    ? 0.0
    : (maxChannel - minChannel) / maxChannel;
  float greenDominance = green - maxRedBlue;
  float keyRamp = max(8.0, u_softness * 0.55);
  float fullKeyStart = u_spillDominance + 12.0;
  float featherStart = max(u_spillDominance, fullKeyStart - keyRamp);
  bool isGreen =
    green == maxChannel &&
    green > u_minGreen &&
    green > red * u_greenBias &&
    green > blue * u_greenBias &&
    saturation > u_minSaturation;

  vec3 color = texel.rgb;
  float alpha = texel.a;

  if (isGreen) {
    float saturationKey = clamp(
      (saturation - u_minSaturation) / 0.35,
      0.0,
      1.0
    );
    float keyedAmount = greenDominance >= fullKeyStart
      ? 1.0
      : smoothstep(featherStart, fullKeyStart, greenDominance);
    keyedAmount *= saturationKey;
    alpha = texel.a * (1.0 - keyedAmount);
  } else if (greenDominance > u_spillDominance && green > u_spillMinGreen) {
    color.g = max(0.0, green - greenDominance * u_spill) / 255.0;
  }

  gl_FragColor = vec4(color * alpha, alpha);
}
`;

function compileShader(gl, type, source) {
  const shader = gl.createShader(type);
  if (!shader) {
    throw new Error('Unable to create WebGL shader');
  }

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) || 'Unknown shader error';
    gl.deleteShader(shader);
    throw new Error(message);
  }

  return shader;
}

function createProgram(gl) {
  const vertexShader = compileShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
  const fragmentShader = compileShader(
    gl,
    gl.FRAGMENT_SHADER,
    fragmentShaderSource
  );
  const program = gl.createProgram();

  if (!program) {
    throw new Error('Unable to create WebGL program');
  }

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const message = gl.getProgramInfoLog(program) || 'Unknown WebGL link error';
    gl.deleteProgram(program);
    throw new Error(message);
  }

  return program;
}

export function createWebGlKeyer(canvas) {
  const gl = canvas.getContext('webgl', {
    alpha: true,
    premultipliedAlpha: true
  });

  if (!gl) return null;

  const program = createProgram(gl);
  const positionBuffer = gl.createBuffer();
  const texCoordBuffer = gl.createBuffer();
  const texture = gl.createTexture();

  if (!positionBuffer || !texCoordBuffer || !texture) {
    throw new Error('Unable to create WebGL buffers');
  }

  gl.useProgram(program);

  const position = gl.getAttribLocation(program, 'a_position');
  const texCoord = gl.getAttribLocation(program, 'a_texCoord');

  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
    gl.STATIC_DRAW
  );
  gl.enableVertexAttribArray(position);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

  gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]),
    gl.STATIC_DRAW
  );
  gl.enableVertexAttribArray(texCoord);
  gl.vertexAttribPointer(texCoord, 2, gl.FLOAT, false, 0, 0);

  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.clearColor(0, 0, 0, 0);

  const uniforms = {
    source: gl.getUniformLocation(program, 'u_source'),
    minGreen: gl.getUniformLocation(program, 'u_minGreen'),
    greenBias: gl.getUniformLocation(program, 'u_greenBias'),
    softness: gl.getUniformLocation(program, 'u_softness'),
    spill: gl.getUniformLocation(program, 'u_spill'),
    minSaturation: gl.getUniformLocation(program, 'u_minSaturation'),
    spillDominance: gl.getUniformLocation(program, 'u_spillDominance'),
    spillMinGreen: gl.getUniformLocation(program, 'u_spillMinGreen')
  };

  gl.uniform1i(uniforms.source, 0);

  return {
    gl,
    program,
    texture,
    positionBuffer,
    texCoordBuffer,
    attributes: {
      position,
      texCoord
    },
    uniforms
  };
}

export function setCanvasSize(canvas, gl, sourceWidth, sourceHeight) {
  const targetWidth = 720;
  const targetHeight = Math.round((targetWidth * sourceHeight) / sourceWidth);

  if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
    canvas.width = targetWidth;
    canvas.height = targetHeight;
  }

  gl.viewport(0, 0, canvas.width, canvas.height);
}

export function isGreenPixel(red, green, blue, settings) {
  const minChannel = Math.min(red, green, blue);
  const maxChannel = Math.max(red, green, blue);
  const saturation =
    maxChannel === 0 ? 0 : (maxChannel - minChannel) / maxChannel;

  return (
    green === maxChannel &&
    green > settings.minGreen &&
    green > red * settings.greenBias &&
    green > blue * settings.greenBias &&
    saturation > settings.minSaturation
  );
}

let greenSampleCanvas = null;
let greenSampleContext = null;

export function measureGreenCoverage(source, settings) {
  const sourceWidth = source?.videoWidth || source?.naturalWidth;
  const sourceHeight = source?.videoHeight || source?.naturalHeight;

  if (!source || !sourceWidth || !sourceHeight) return 0;

  const sampleWidth = 64;
  const sampleHeight = Math.max(
    1,
    Math.round((sampleWidth * sourceHeight) / sourceWidth)
  );

  if (!greenSampleCanvas) {
    greenSampleCanvas = document.createElement('canvas');
    greenSampleContext = greenSampleCanvas.getContext('2d', {
      willReadFrequently: true
    });
  }

  if (!greenSampleContext) return 0;

  greenSampleCanvas.width = sampleWidth;
  greenSampleCanvas.height = sampleHeight;
  greenSampleContext.drawImage(source, 0, 0, sampleWidth, sampleHeight);

  const { data } = greenSampleContext.getImageData(
    0,
    0,
    sampleWidth,
    sampleHeight
  );
  let greenCount = 0;
  const totalPixels = data.length / 4;

  for (let i = 0; i < data.length; i += 4) {
    if (isGreenPixel(data[i], data[i + 1], data[i + 2], settings)) {
      greenCount += 1;
    }
  }

  return totalPixels === 0 ? 0 : greenCount / totalPixels;
}

export function destroyKeyer(keyer) {
  keyer.gl.deleteTexture(keyer.texture);
  keyer.gl.deleteBuffer(keyer.positionBuffer);
  keyer.gl.deleteBuffer(keyer.texCoordBuffer);
  keyer.gl.deleteProgram(keyer.program);
}

export function drawSource(source, canvas, settings, gl, keyer) {
  const sourceWidth = source ? source.videoWidth : source.naturalWidth;
  const sourceHeight = source ? source.videoHeight : source.naturalHeight;

  if (!canvas || !sourceWidth || !sourceHeight) return;

  setCanvasSize(canvas, gl, sourceWidth, sourceHeight);
  gl.useProgram(keyer.program);
  gl.bindBuffer(gl.ARRAY_BUFFER, keyer.positionBuffer);
  gl.vertexAttribPointer(keyer.attributes.position, 2, gl.FLOAT, false, 0, 0);
  gl.bindBuffer(gl.ARRAY_BUFFER, keyer.texCoordBuffer);
  gl.vertexAttribPointer(keyer.attributes.texCoord, 2, gl.FLOAT, false, 0, 0);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, keyer.texture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
  gl.uniform1f(keyer.uniforms.minGreen, settings.minGreen);
  gl.uniform1f(keyer.uniforms.greenBias, settings.greenBias);
  gl.uniform1f(keyer.uniforms.softness, settings.softness);
  gl.uniform1f(keyer.uniforms.spill, settings.spill);
  gl.uniform1f(keyer.uniforms.minSaturation, settings.minSaturation);
  gl.uniform1f(keyer.uniforms.spillDominance, settings.spillDominance);
  gl.uniform1f(keyer.uniforms.spillMinGreen, settings.spillMinGreen);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
}
